import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { injectQuery } from '@tanstack/angular-query-experimental';
import { lastValueFrom, take } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { challenge as ChallengeGen } from '@valvur/shared-grpc';
import type { IdentityAdminOverview } from '../../identity/services/identity-layer-admin.service';
import type { ListDailyFactsResponse } from '../../admin/daily-facts/services/daily-facts.service';
import type { PatrolRobloxFleet } from '../../admin/patrol/services/patrol.service';
import type { PatrolSetup } from '../../admin/patrol/services/patrol.service';
import { PATROL_PLATFORM_FLAG_KEY } from '../../patrol/constants/patrol-platform-flag-keys.constants';
import { FeatureFlagsAdminService } from '../../../shared/services/feature-flags-admin.service';

const DRAFT_STATUSES = new Set(['DRAFT', 'READY_FOR_VIDEO_GENERATION']);

@Injectable({ providedIn: 'root' })
export class OfficeDashboardService {
  #http = inject(HttpClient);
  #featureFlagsAdmin = inject(FeatureFlagsAdminService);

  readonly fetchEnabled = signal(false);

  readonly coursesQuery = injectQuery(() => ({
    queryKey: ['officeDashboard', 'valvurCourses'],
    enabled: this.fetchEnabled(),
    staleTime: 60_000,
    retry: 1,
    queryFn: () =>
      lastValueFrom(
        this.#http
          .get<ChallengeGen.CourseRes>(`${environment.baseUrl}/courses/valvur`)
          .pipe(take(1)),
      ),
  }));

  readonly identityQuery = injectQuery(() => ({
    queryKey: ['officeDashboard', 'identityOverview'],
    enabled: this.fetchEnabled(),
    staleTime: 60_000,
    retry: 1,
    queryFn: () =>
      lastValueFrom(
        this.#http
          .get<IdentityAdminOverview>(
            `${environment.baseUrl}/user/identity/admin/overview`,
          )
          .pipe(take(1)),
      ),
  }));

  readonly dailyFactsQuery = injectQuery(() => ({
    queryKey: ['officeDashboard', 'dailyFacts'],
    enabled: this.fetchEnabled(),
    staleTime: 30_000,
    retry: 1,
    queryFn: () =>
      lastValueFrom(
        this.#http
          .get<ListDailyFactsResponse>(
            `${environment.baseUrl}/activities/daily-facts`,
            { params: { limit: '100', offset: '0' } },
          )
          .pipe(take(1)),
      ),
  }));

  readonly featureFlagsQuery = injectQuery(() => ({
    queryKey: ['officeDashboard', 'featureFlags'],
    enabled: this.fetchEnabled(),
    staleTime: 60_000,
    retry: 1,
    queryFn: () => this.#featureFlagsAdmin.list(),
  }));

  readonly patrolSetupQuery = injectQuery(() => ({
    queryKey: ['officeDashboard', 'patrolSetup'],
    enabled: this.fetchEnabled(),
    staleTime: 60_000,
    retry: 1,
    queryFn: () =>
      lastValueFrom(
        this.#http
          .get<PatrolSetup>(`${environment.baseUrl}/bots/discord/patrol/setup`)
          .pipe(take(1)),
      ),
  }));

  readonly patrolRobloxFleetQuery = injectQuery(() => ({
    queryKey: ['officeDashboard', 'patrolRobloxFleet'],
    enabled: this.fetchEnabled(),
    staleTime: 60_000,
    retry: 1,
    queryFn: () =>
      lastValueFrom(
        this.#http
          .get<PatrolRobloxFleet>(
            `${environment.baseUrl}/bots/patrol/roblox-fleet`,
          )
          .pipe(take(1)),
      ),
  }));

  readonly isLoading = computed(
    () =>
      this.coursesQuery.isLoading() ||
      this.identityQuery.isLoading() ||
      this.dailyFactsQuery.isLoading() ||
      this.featureFlagsQuery.isLoading() ||
      this.patrolSetupQuery.isLoading() ||
      this.patrolRobloxFleetQuery.isLoading(),
  );

  readonly publishedCourses = computed(() => {
    const courses = this.coursesQuery.data()?.data ?? [];
    return courses.filter((course) => course.status === 'PUBLISHED').length;
  });

  readonly pipelineCourses = computed(() => {
    const courses = this.coursesQuery.data()?.data ?? [];
    return courses.filter((course) => DRAFT_STATUSES.has(course.status)).length;
  });

  readonly pendingFactVideos = computed(() => {
    const facts = this.dailyFactsQuery.data()?.facts ?? [];
    return facts.filter((fact) => fact.videoStatus === 'PENDING').length;
  });

  readonly enabledFeatureFlags = computed(() => {
    const flags = this.featureFlagsQuery.data() ?? [];
    return flags.filter((flag) => flag.enabled).length;
  });

  readonly identityLayerEnabled = computed(
    () => this.identityQuery.data()?.layerEnabled ?? false,
  );

  readonly identityGraceDays = computed(
    () => this.identityQuery.data()?.legacyGraceDays ?? 0,
  );

  readonly identityKycProvider = computed(
    () => this.identityQuery.data()?.kycProvider ?? '—',
  );

  readonly patrolDiscordEnabled = computed(
    () => this.#isFlagEnabled(PATROL_PLATFORM_FLAG_KEY.DISCORD),
  );

  readonly patrolRobloxEnabled = computed(
    () => this.#isFlagEnabled(PATROL_PLATFORM_FLAG_KEY.ROBLOX),
  );

  readonly patrolDiscordGuildCount = computed(
    () => this.patrolSetupQuery.data()?.guildCount ?? 0,
  );

  readonly patrolDiscordBotReady = computed(
    () => this.patrolSetupQuery.data()?.botReady ?? false,
  );

  readonly patrolRobloxExperienceCount = computed(
    () => this.patrolRobloxFleetQuery.data()?.experienceCount ?? 0,
  );

  prefetch(): void {
    if (this.fetchEnabled()) {
      return;
    }
    this.fetchEnabled.set(true);
  }

  #isFlagEnabled(key: string): boolean {
    return (
      this.featureFlagsQuery.data()?.find((flag) => flag.key === key)
        ?.enabled ?? false
    );
  }
}
